import { TReservationSearchOptions } from "../types/components/SearchOptions";
import React, { useEffect, useRef, useState } from "react";
import { MillenniumDate, MillenniumDateTime } from "@timeedit/millennium-time";
import CancellationListSettings from "./CancellationListSettings";
import Language from "../lib/Language";
import _ from "underscore";
import TemplateKind from "../models/TemplateKind";
import ReservationTypeList from "./ReservationTypeList";
import RC from "../lib/ReservationConstants";
import Log from "../lib/Log";
import { TimeEdit } from "../lib/TimeEdit";
import { Macros } from "../models/Macros";
import TimeEditAPI from "../lib/TimeEditAPI";
import { useAppContext } from "../contexts/AppContext";
import { CancellationSearch } from "../models/CancellationSearch";
import { Limits } from "../models/Limits";
import {
    TListCalendarInjectedProps,
    TListCalendarWrapperProps,
} from "../types/components/ListCalendarWrapper";
import { TComponent } from "../types/utils";

const MAX_CANCELLATIONS_TO_RESTORE = 5000;

const COL_IDS = {
    ID: 0,
    DATE: 1,
    TIME: 2,
    TYPES: 3,
    FIELDS: 4,
    CANCELLED: 5,
    WEEK: 8,
    WEEKDAY: 9,
    LENGTH: 10,
    STATUS: 11,
};

const getAvailableColumns = (context) => {
    const columns = [
        { name: Language.get("cal_reservation_list_column_id"), sortable: true, id: COL_IDS.ID },
        {
            name: Language.get("cal_reservation_list_column_date"),
            sortable: true,
            id: COL_IDS.DATE,
        },
        {
            name: Language.get("cal_reservation_list_column_time"),
            sortable: false,
            id: COL_IDS.TIME,
        },
        {
            name: Language.get("cal_reservation_list_column_type_all"),
            sortable: false,
            id: COL_IDS.TYPES,
        },
        {
            name: Language.get("cal_reservation_list_column_field_all"),
            sortable: false,
            id: COL_IDS.FIELDS,
        },
        {
            name: Language.get("cal_reservation_list_column_cancelled"),
            sortable: true,
            id: COL_IDS.CANCELLED,
        },
        {
            name: Language.get("cal_reservation_list_column_week"),
            sortable: false,
            id: COL_IDS.WEEK,
        },
        {
            name: Language.get("cal_reservation_list_column_week_day"),
            sortable: false,
            id: COL_IDS.WEEKDAY,
        },
        {
            name: Language.get("cal_reservation_list_column_length"),
            sortable: false,
            id: COL_IDS.LENGTH,
        },
        {
            name: Language.get("cal_reservation_list_column_status"),
            sortable: false,
            id: COL_IDS.STATUS,
        },
        {
            name: Language.get("nc_reservation_group_list_title"),
            sortable: false,
            id: 12,
        },
    ];
    if (true) {
        // TODO Check if timezone column should be active
        columns.push({
            name: Language.get("cal_res_side_user_time_zone"),
            sortable: false,
            id: 15,
        });
    }
    return columns;
};

type TCancellationListProps = TListCalendarWrapperProps<CancellationSearch> &
    TListCalendarInjectedProps;

const DEFAULT_TIME_LIMITS = () => ({
    begin: MillenniumDateTime.fromDate(MillenniumDate.today()).getMts(),
    end: 0,
});

// `data` arrives via ListCalendar's cloneElement, so it is absent when the child
// is rendered directly. The class relied on the try/catch swallowing the
// resulting TypeError; the guard makes that explicit.
const getTimeLimits = (data?: { limits?: Limits }) => {
    if (!data?.limits) {
        return DEFAULT_TIME_LIMITS();
    }
    try {
        return {
            begin: MillenniumDateTime.fromDate(data.limits.getStartDate()).getMts(),
            end: MillenniumDateTime.fromDate(data.limits.getEndDate()).getMts(),
        };
    } catch (error) {
        return DEFAULT_TIME_LIMITS();
    }
};

const CancellationList: TComponent<TCancellationListProps> = (props) => {
    const appContext = useAppContext();

    const [state, setState] = useState(() => {
        const timeLimit = getTimeLimits(props.data);
        return {
            searchOptions: {
                templateKind: TemplateKind.RESERVATION,
                searchString: "",
                beginTime: timeLimit.begin,
                endTime: timeLimit.end,
                name: "",
                allReservations: false,
                reservationStatus: [
                    RC.STATUS.CONFIRMED,
                    RC.STATUS.PRELIMINARY,
                    RC.STATUS.PLANNED,
                    RC.STATUS.REQUESTED,
                    RC.STATUS.REJECTED,
                ],
                includeMembers: RC.MEMBERS.EXCLUDE,
                useTemplateGroup: false,
                reservationVariant: RC.VARIANT.CANCELLED,
                complete: true,
                incomplete: true,
                permission: RC.PERMISSION.READ,
            } as TReservationSearchOptions,
            canDeleteCancellations: false,
        };
    });

    useEffect(() => {
        TimeEditAPI.okToDeleteReservationsCancelled([], (okToDelete) => {
            setState((prev) => ({
                ...prev,
                canDeleteCancellations: Array.isArray(okToDelete)
                    ? okToDelete.every(Boolean)
                    : okToDelete,
            }));
        });
    }, []);

    const prevLimits = useRef(props.data?.limits);
    useEffect(() => {
        if (prevLimits.current === props.data?.limits) {
            return;
        }
        prevLimits.current = props.data?.limits;
        const timeLimits = getTimeLimits(props.data);
        setState((prev) => {
            if (
                timeLimits.begin === prev.searchOptions.beginTime &&
                timeLimits.end === prev.searchOptions.endTime
            ) {
                return prev;
            }
            return {
                ...prev,
                searchOptions: _.extend({}, prev.searchOptions, {
                    beginTime: timeLimits.begin,
                    endTime: timeLimits.end,
                }),
            };
        });
    });

    // No current caller supplies `cb`; kept for signature compatibility.
    const onSearchSettingsChange = (settings, cb?: () => void) => {
        let newSettings = _.extend(
            {},
            _.omit(settings, (value) => value === undefined)
        );
        // If user is not an admin, enforce the default value for hidden search property
        if (!appContext.user?.isAdmin) {
            newSettings = _.extend(newSettings, {
                includeMembers: RC.MEMBERS.EXCLUDE,
            });
        }

        // Ensure cancellations are shown no matter their status
        newSettings = _.extend(newSettings, {
            reservationStatus: [
                RC.STATUS.CONFIRMED,
                RC.STATUS.PRELIMINARY,
                RC.STATUS.PLANNED,
                RC.STATUS.REQUESTED,
                RC.STATUS.REJECTED,
            ],
            reservationVariant: RC.VARIANT.CANCELLED,
        });

        setState((prev) => ({
            ...prev,
            searchOptions: _.extend({}, prev.searchOptions, newSettings),
        }));
        if (cb) {
            cb();
        }
    };

    const editFields = (selectedReservationIds) => {
        props.onInfoOpen?.(selectedReservationIds, true, true);
    };

    const getReservationMenuItems = (selectedReservationIds, getDataAtIndex, refresh) => {
        const isAllowedToRestoreCancellations =
            appContext.user?.isAdmin || !state.searchOptions.allReservations;
        if (!isAllowedToRestoreCancellations) {
            return [];
        }

        let label = Language.get("nc_restore_cancellation");
        if (selectedReservationIds.length > 1) {
            label = Language.get("nc_restore_cancellations", selectedReservationIds.length);
        }
        const items: any[] = [];
        const deleteAction = () => {
            const ids = [].concat(selectedReservationIds);
            TimeEditAPI.okToDeleteReservationsCancelled(ids, (okResult) => {
                // Asked with reservations, so this is one answer per reservation.
                const answers = Array.isArray(okResult) ? okResult : [okResult];
                if (_.every(answers, (res) => res === true)) {
                    // eslint-disable-next-line no-unused-vars
                    TimeEditAPI.deleteReservationsCancelled(ids, (result) => {
                        refresh();
                    });
                } else {
                    Log.warning(Language.get("cal_res_below_delete_cancellations_failed"));
                }
            });
        };
        const restoreAction = (allowDoubleReservation) => {
            const ids = [].concat(selectedReservationIds);
            TimeEditAPI.restoreReservations(ids, allowDoubleReservation, (response) => {
                const errors = response.parameters[0]
                    .map((status, i) => _.extend(status, { originalId: ids[i] }))
                    .filter((status) => Boolean(status.details));
                const successes = response.parameters[0].filter(
                    (status) => !Boolean(status.details)
                );

                if (errors.length === ids.length) {
                    Log.warning(errors[0].details);
                    return;
                }

                appContext.fireEvent(
                    `reservationList${props.id}`,
                    Macros.Event.RESERVATION_RESTORED,
                    successes[0].reference
                );
                refresh();

                let successMessage = Language.get("nc_restore_cancellation_complete");
                if (successes.length !== 1) {
                    successMessage = Language.get(
                        "nc_restore_cancellations_complete",
                        successes.length
                    );
                }

                if (successes.length === ids.length) {
                    Log.info(successMessage);
                    return;
                }

                const modalContent = (
                    <div>
                        <p>{Language.get("nc_restore_cancellations_complete", successes.length)}</p>
                        <div>
                            {Language.get(
                                "nc_restore_cancellations_list_of_errors_title",
                                errors.length
                            )}
                            :
                            <ul>
                                {errors.map((error) => (
                                    <li>
                                        <strong>{error.originalId}</strong>: {error.details}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                );
                appContext.presentModal(modalContent);
            });
        };
        items.push(
            {
                label: Language.get("nc_cal_func_res_edit_fields"),
                isDisabled:
                    selectedReservationIds.length > MAX_CANCELLATIONS_TO_RESTORE ||
                    selectedReservationIds.length === 0,
                action: () => {
                    editFields(selectedReservationIds);
                },
                classNames: ["icon", "editFields"],
                shortcut: TimeEdit.presentShortcut("mod+e"),
            },
            {
                label,
                isDisabled:
                    selectedReservationIds.length === 0 ||
                    selectedReservationIds.length > MAX_CANCELLATIONS_TO_RESTORE,
                action: () => {
                    restoreAction(false);
                },
                submenu: [
                    {
                        label,
                        isDisabled:
                            selectedReservationIds.length === 0 ||
                            selectedReservationIds.length > MAX_CANCELLATIONS_TO_RESTORE,
                        action: () => {
                            restoreAction(false);
                        },
                    },
                    {
                        label: `${label} (${Language.get(
                            "cal_selected_allow_double_res"
                        ).toLowerCase()})`,
                        isDisabled:
                            selectedReservationIds.length === 0 ||
                            selectedReservationIds.length > MAX_CANCELLATIONS_TO_RESTORE,
                        action: () => {
                            restoreAction(true);
                        },
                    },
                ],
            }
        );
        if (state.canDeleteCancellations) {
            items.push({
                label: `${Language.get("cal_res_below_delete_cancellations_title")} (${
                    selectedReservationIds.length
                })`,
                submenu: [
                    {
                        label: `${Language.get("cal_res_below_delete_cancellations_title")} (${
                            selectedReservationIds.length
                        })`,
                        action: () => {
                            deleteAction();
                        },
                    },
                ],
            });
        }
        return items;
    };

    const getSearchHelp = () => {
        return (
            <div className="reservationSearchHelp">
                <table>
                    <tbody>
                        <tr>
                            <td>#</td>
                            <td>{Language.get("nc_search_help_reservation_id")}</td>
                        </tr>
                        <tr>
                            <td>##</td>
                            <td>{Language.get("nc_search_help_object_id")}</td>
                        </tr>
                        <tr>
                            <td>@</td>
                            <td>{Language.get("nc_search_help_cancelled_by")}</td>
                        </tr>
                        <tr>
                            <td>d:</td>
                            <td>{Language.get("nc_search_help_cancellation_date")}</td>
                        </tr>
                        <tr>
                            <td>t:</td>
                            <td>{Language.get("nc_search_help_has_objects_of_type")}</td>
                        </tr>
                        <tr>
                            <td>p:</td>
                            <td>{Language.get("nc_search_help_has_placeholder_for_type")}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    const getSettingsComponent = (
        saveDefaultSettings,
        saveSettings,
        getSavedSettings,
        removeSettings
    ) => {
        const opts = state.searchOptions;

        return (
            <CancellationListSettings
                onChange={onSearchSettingsChange}
                isAdmin={appContext.user?.isAdmin ?? false}
                allReservations={opts.allReservations}
                reservationStatus={opts.reservationStatus}
                includeMembers={opts.includeMembers}
                saveDefaultSettings={saveDefaultSettings}
                saveSettings={saveSettings}
                removeSettings={removeSettings}
                getSavedSettings={getSavedSettings}
                reservationVariant={opts.reservationVariant}
                templateKind={opts.templateKind}
                complete={opts.complete}
                incomplete={opts.incomplete}
                useTemplateGroup={opts.useTemplateGroup}
            />
        );
    };

    const classes = {
        cancellationList: true,
    };

    return (
        <ReservationTypeList
            {...props}
            onSearchSettingsChange={onSearchSettingsChange}
            searchOptions={state.searchOptions}
            defaultStatus={RC.STATUS.TIME}
            columns={getAvailableColumns(appContext)}
            defaultSelectedColumns={[COL_IDS.DATE, COL_IDS.TIME, COL_IDS.TYPES, COL_IDS.CANCELLED]}
            getDataMenuItems={getReservationMenuItems}
            getSettingsComponent={getSettingsComponent}
            title={Language.get("nc_cancellation_list_title")}
            classes={classes}
            allowMultiSelection
            supportsEmail
            addTypeColumns={TemplateKind.equals(
                TemplateKind.RESERVATION,
                state.searchOptions.templateKind
            )}
            getSearchHelp={getSearchHelp}
        />
    );
};

export default CancellationList;
